"use client";

import { useEffect, useLayoutEffect, useState } from "react";
import { useRouter } from "next/navigation";
import type { FeedFilterState } from "@/hooks/use-paginated-feed";
import {
  FEED_DEFAULT_ACTIVE_TAB,
  coerceFeedActiveTab,
  isFeedSnapStyleTab,
} from "@/lib/feed-tab-config";
import {
  FEED_ACTIVE_TAB_STORAGE_KEY,
  FEED_ACTIVE_TAB_TTL_MS,
  decideRestoredFeedActiveTab,
  serializeFeedActiveTab,
} from "@/lib/feed/feed-active-tab-persistence";
import {
  FEED_DEFAULT_FILTERS,
  FEED_FILTERS_STORAGE_KEY,
  FEED_FILTERS_TTL_MS,
  decideRestoredFeedFilters,
  serializeFeedFilters,
} from "@/lib/feed/feed-filters-persistence";

export type FeedActiveTab = "for-you" | "following" | "live" | "discover";

export type FeedSortOption = "price-asc" | "price-desc" | "date-asc" | "date-desc" | null;

export type FeedFilters = FeedFilterState & {
  sortBy: FeedSortOption;
};

/**
 * Restore the active tab SYNCHRONOUSLY in the lazy initializer (no post-mount
 * flash). Safe from hydration mismatch: `FeedPageClient` (feed-page-client.tsx)
 * mounts this hook's owner only after an `isDesktop !== null` client-only gate
 * — the server-rendered HTML never contains this subtree, so there is nothing
 * to mismatch against. @see lib/feed/feed-active-tab-persistence.ts
 */
function readInitialActiveTab(isLoggedIn: boolean): FeedActiveTab {
  if (typeof window === "undefined") return FEED_DEFAULT_ACTIVE_TAB;
  const raw = window.sessionStorage.getItem(FEED_ACTIVE_TAB_STORAGE_KEY);
  return decideRestoredFeedActiveTab(raw, Date.now(), FEED_ACTIVE_TAB_TTL_MS, isLoggedIn) ?? FEED_DEFAULT_ACTIVE_TAB;
}

/**
 * Restore filters SYNCHRONOUSLY in the lazy initializer — same rationale as
 * `readInitialActiveTab` above (no post-mount flash; safe from hydration
 * mismatch because this hook's owner only mounts after the `isDesktop !== null`
 * client-only gate in `feed-page-client.tsx`). Restoring filters here is what
 * lets the existing feed-snapshot cache (`lib/feed/feed-snapshot.ts`) hit on
 * back-nav: its cache key embeds the filters, so if filters reset to default
 * on remount, the snapshot lookup misses even though the snapshot itself is
 * still in storage. @see lib/feed/feed-filters-persistence.ts
 */
function readInitialFilters(): FeedFilters {
  if (typeof window === "undefined") return FEED_DEFAULT_FILTERS;
  const raw = window.sessionStorage.getItem(FEED_FILTERS_STORAGE_KEY);
  return decideRestoredFeedFilters(raw, Date.now(), FEED_FILTERS_TTL_MS) ?? FEED_DEFAULT_FILTERS;
}

export function useFeedPageClientState(isLoggedIn = true) {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<FeedActiveTab>(() => readInitialActiveTab(isLoggedIn));

  const [isTransitioning, setIsTransitioning] = useState(false);
  // Deliberately NOT persisted (unlike activeTab above). This is a pure
  // user-intent signal — "the user explicitly re-tapped/switched into
  // live/discover, reroll + scroll to top" — only incremented inside
  // handleTabChange below. A restored mount after back-nav always starts at 0
  // (a fresh value the child has never seen), and live-feed.tsx /
  // discover-feed-v2.tsx skip the FIRST signal value they observe
  // (`refreshInitializedRef`), so a tab restored via readInitialActiveTab
  // never triggers a reroll — only an explicit tab switch/re-tap does.
  const [discoverRefreshKey, setDiscoverRefreshKey] = useState(0);
  const [filters, setFilters] = useState<FeedFilters>(() => readInitialFilters());
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);

  // Persist the active tab on every change (best-effort) so a remount after
  // back-nav (readInitialActiveTab above) restores it. Also re-runs on mount
  // with the restored value, refreshing its TTL for as long as the session
  // stays on the feed page.
  useEffect(() => {
    try {
      sessionStorage.setItem(FEED_ACTIVE_TAB_STORAGE_KEY, serializeFeedActiveTab(activeTab, Date.now()));
    } catch {
      /* private mode */
    }
  }, [activeTab]);

  // Persist filters on every change (best-effort) so a remount after
  // back-nav (readInitialFilters above) restores them — and, transitively,
  // lets the feed-snapshot cache key match again. Also re-runs on mount with
  // the restored value, refreshing its TTL for as long as the session stays
  // on the feed page (same rationale as the activeTab effect above).
  useEffect(() => {
    try {
      sessionStorage.setItem(FEED_FILTERS_STORAGE_KEY, serializeFeedFilters(filters, Date.now()));
    } catch {
      /* private mode */
    }
  }, [filters]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevHtmlOverscroll = (html.style as any).overscrollBehaviorY as string | undefined;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyOverscroll = (body.style as any).overscrollBehaviorY as string | undefined;
    const prevBodyHeight = body.style.height;
    const shouldLock = isFeedSnapStyleTab(activeTab);

    if (shouldLock) {
      html.style.overflow = "hidden";
      (html.style as any).overscrollBehaviorY = "none";
      body.style.overflow = "hidden";
      (body.style as any).overscrollBehaviorY = "none";
      body.style.height = "100dvh";
    } else {
      html.style.overflow = prevHtmlOverflow;
      (html.style as any).overscrollBehaviorY = prevHtmlOverscroll ?? "";
      body.style.overflow = prevBodyOverflow;
      (body.style as any).overscrollBehaviorY = prevBodyOverscroll ?? "";
      body.style.height = prevBodyHeight;
    }

    return () => {
      html.style.overflow = "";
      (html.style as any).overscrollBehaviorY = "";
      body.style.overflow = "";
      (body.style as any).overscrollBehaviorY = "";
      body.style.height = "";
    };
  }, [activeTab]);

  const runTabTransition = (after: () => void) => {
    setIsTransitioning(true);
    window.setTimeout(() => {
      after();
      window.setTimeout(() => setIsTransitioning(false), 60);
    }, 140);
  };

  const handleTabChange = (value: FeedActiveTab | ((prev: FeedActiveTab) => FeedActiveTab)) => {
    const requested = typeof value === "function" ? value(activeTab) : value;
    const nextTab = coerceFeedActiveTab(requested, isLoggedIn);
    if (nextTab === activeTab) {
      if (nextTab === "live" || nextTab === "discover") {
        setDiscoverRefreshKey((prev) => prev + 1);
      }
      return;
    }
    runTabTransition(() => {
      setActiveTab(nextTab);
      if (nextTab === "live" || nextTab === "discover") {
        setDiscoverRefreshKey((prev) => prev + 1);
      }
    });
  };

  useLayoutEffect(() => {
    const root = document.documentElement;
    const prevRoute = root.dataset.kraahlRoute;
    root.dataset.kraahlRoute = "feed";

    const header = document.querySelector("[data-global-header]") as HTMLElement | null;
    const mainContent = document.querySelector("main > div") as HTMLElement | null;

    const prevHeaderDisplay = header?.style.display ?? "";
    const prevMainPaddingTop = mainContent?.style.paddingTop ?? "";
    const prevMainPaddingBottom = mainContent?.style.paddingBottom ?? "";
    const prevMainMinHeight = mainContent?.style.minHeight ?? "";

    if (header) {
      header.style.display = "none";
    }

    if (mainContent) {
      mainContent.style.paddingTop = "0px";
      mainContent.style.paddingBottom = "0px";
      mainContent.style.minHeight = "0px";
    }

    const updateFeedTopBarHeight = () => {
      const feedTopBar = document.querySelector("[data-feed-top-bar]") as HTMLElement | null;
      if (feedTopBar) {
        const height = feedTopBar.offsetHeight;
        document.documentElement.style.setProperty("--feed-top-bar-height", `${height}px`);
      }
    };

    const initHeight = () => {
      return window.setTimeout(updateFeedTopBarHeight, 0);
    };

    const initTimer = initHeight();

    const feedTopBarResizeObserver = new ResizeObserver(() => {
      updateFeedTopBarHeight();
    });
    const feedTopBar = document.querySelector("[data-feed-top-bar]") as HTMLElement | null;
    if (feedTopBar) {
      feedTopBarResizeObserver.observe(feedTopBar);
    }

    const updatePaddingAdjust = () => {
      const contestBannerHeight =
        getComputedStyle(document.documentElement).getPropertyValue("--contest-banner-height") || "0px";
      const customBannerHeight =
        getComputedStyle(document.documentElement).getPropertyValue("--custom-banner-height") || "0px";
      document.documentElement.style.setProperty(
        "--route-padding-adjust",
        `calc(-1 * (${contestBannerHeight} + ${customBannerHeight}))`
      );
    };

    updatePaddingAdjust();

    const observer = new MutationObserver(() => {
      updatePaddingAdjust();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });

    const contestBanner = document.getElementById("kraahl-contest-banner");
    const customBanner = document.getElementById("kraahl-custom-banner");
    const resizeObserver = new ResizeObserver(updatePaddingAdjust);
    if (contestBanner) resizeObserver.observe(contestBanner);
    if (customBanner) resizeObserver.observe(customBanner);

    const displayObserver = new MutationObserver(updatePaddingAdjust);
    if (contestBanner) {
      displayObserver.observe(contestBanner, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    }
    if (customBanner) {
      displayObserver.observe(customBanner, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    }

    return () => {
      window.clearTimeout(initTimer);
      if (header) {
        header.style.display = prevHeaderDisplay;
      }
      if (mainContent) {
        mainContent.style.paddingTop = prevMainPaddingTop;
        mainContent.style.paddingBottom = prevMainPaddingBottom;
        mainContent.style.minHeight = prevMainMinHeight;
      }
      feedTopBarResizeObserver.disconnect();
      observer.disconnect();
      resizeObserver.disconnect();
      displayObserver.disconnect();
      document.documentElement.style.removeProperty("--route-padding-adjust");
      document.documentElement.style.removeProperty("--feed-top-bar-height");
      if (typeof prevRoute === "string" && prevRoute.length > 0 && prevRoute !== "feed") {
        root.dataset.kraahlRoute = prevRoute;
      } else {
        delete root.dataset.kraahlRoute;
      }
    };
  }, []);

  const handleSearch = (query: string) => {
    router.push(`/search?query=${encodeURIComponent(query)}`);
  };

  return {
    activeTab,
    discoverRefreshKey,
    filters,
    handleSearch,
    handleTabChange,
    isMobileSheetOpen,
    isTransitioning,
    setFilters,
    setIsMobileSheetOpen,
  };
}

